// Demo products and services, as ledger.products_list / save_product /
// import_products (db/025_products.sql): a price list, no stock.
import type { ContactProduct, LedgerSource, Product, ProductInput } from './source';

type Part = Pick<LedgerSource, 'products' | 'saveProduct' | 'setProductActive' | 'deleteProduct' | 'importProducts' | 'contactProducts' | 'setContactProduct'>;
const delay = <T,>(v: T, ms = 60): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (m: string) => Promise.reject(new Error(m));

const SALES_ACCOUNTS = ['4000', '4010', '4020', '4900'];
const PURCHASE_ACCOUNTS = ['5000', '5100', '6000', '7000', '7100', '7200', '7300', '7400', '7600', '7800', '7900'];
const CODES = ['S23', 'R135', 'R9', 'L48', 'Z', 'EX', 'RCT', 'EUGS', 'EUSS', 'EUGA', 'EUSR', 'PVA'];

const item = (code: string, description: string, unit: string, sales: string | null, purchase: string | null,
  sa: string | null, pa: string | null, st: string | null, pt: string | null, in_use = false): Product => ({
  code, description, unit, sales_price: sales, purchase_price: purchase, sales_account: sa, purchase_account: pa,
  sales_tax_code: st, purchase_tax_code: pt, is_active: true, source_system: null, in_use, updated_at: '2026-10-01T09:00:00Z',
});

function seed(): Product[] {
  return [
    item('DET-OPT', 'Optical smoke detector', 'each', '42.5000', '18.2000', '4000', '5000', 'S23', 'S23', true),
    item('DET-HEAT', 'Heat detector, rate of rise', 'each', '38.0000', '15.9000', '4000', '5000', 'S23', 'S23'),
    item('SND-WALL', 'Addressable wall sounder', 'each', '42.5000', '19.7500', '4000', '5000', 'S23', 'S23', true),
    item('NET2-CTRL', 'Paxton Net2 controller', 'each', '189.0000', '121.0000', '4000', '5000', 'S23', 'S23', true),
    item('SVC-HR', 'Engineer, per hour', 'hour', '65.0000', null, '4010', null, 'R135', null),
    item('SVC-SERVICE-Q', 'Quarterly fire alarm service', 'visit', '285.0000', null, '4020', null, 'R135', null, true),
    item('MON-MONTH', 'Alarm monitoring, per month', 'month', '85.0000', null, '4020', null, 'R135', null, true),
  ];
}

// A customer's own values: Dublin Hotels Group (c4) has a contract price and their own part numbers.
type Theirs = { price: string | null; their_code: string | null; their_description: string | null };
const seedTheirs = (): Record<string, Record<string, Theirs>> => ({
  c4: {
    'DET-OPT': { price: '39.0000', their_code: 'DHG-4471', their_description: null },
    'SVC-SERVICE-Q': { price: '260.0000', their_code: null, their_description: 'Quarterly service (framework agreement DHG-2024-07)' },
  },
});

let store: Product[] = seed();
let theirs = seedTheirs();
export function resetDemoProducts() { store = seed(); theirs = seedTheirs(); }

function theirList(contactId: string): ContactProduct[] {
  return Object.entries(theirs[contactId] ?? {}).map(([code, t]) => {
    const p = demoProduct(code)!;
    return { code: p.code, description: p.description, unit: p.unit ?? null, list_price: p.sales_price ?? null, ...t, is_active: p.is_active };
  }).sort((a, b) => a.code.localeCompare(b.code));
}

/** For the editors in the demo: the item behind a code. */
export function demoProduct(code: string): Product | undefined {
  return store.find((p) => p.code.toLowerCase() === code.toLowerCase());
}

const amount = (v: string | null | undefined, label: string): string | null | Error => {
  if (v == null || String(v).trim() === '') return null;
  const n = Number(String(v).replace(/[,€\s]/g, ''));
  if (Number.isNaN(n)) return new Error(`The ${label} should be an amount such as 42.50`);
  if (n < 0) return new Error(`The ${label} can’t be negative`);
  return n.toFixed(4);
};

function apply(input: ProductInput, row = ''): Product | Error {
  const code = (input.code ?? '').trim();
  if (!/^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$/.test(code)) return new Error(`${row}A code is up to 40 letters, digits, dots, dashes or slashes, such as DET-OPT-01`);
  const existing = demoProduct(code);
  if (!existing && !input.description?.trim()) return new Error(`${row}The item needs a description`);
  if (input.description !== undefined && !input.description.trim()) return new Error(`${row}The item needs a description`);
  const next: Product = existing ? { ...existing } : { code, description: '', unit: null, sales_price: null, purchase_price: null, sales_account: null,
    purchase_account: null, sales_tax_code: null, purchase_tax_code: null, is_active: true, source_system: null, in_use: false };
  if (input.description !== undefined) next.description = input.description.trim();
  if (input.unit !== undefined) next.unit = input.unit?.trim() || null;
  for (const [k, label] of [['sales_price', 'sales price'], ['purchase_price', 'purchase price']] as const) {
    if (input[k] !== undefined) {
      const v = amount(input[k], label);
      if (v instanceof Error) return new Error(row + v.message);
      next[k] = v;
    }
  }
  if (input.sales_account !== undefined) {
    if (input.sales_account && !SALES_ACCOUNTS.includes(input.sales_account)) return new Error(`${row}Account ${input.sales_account} isn’t an income account`);
    next.sales_account = input.sales_account || null;
  }
  if (input.purchase_account !== undefined) {
    if (input.purchase_account && !PURCHASE_ACCOUNTS.includes(input.purchase_account)) return new Error(`${row}Account ${input.purchase_account} isn’t an expense account`);
    next.purchase_account = input.purchase_account || null;
  }
  for (const k of ['sales_tax_code', 'purchase_tax_code'] as const) {
    if (input[k] !== undefined) {
      const c = input[k]?.trim().toUpperCase() || null;
      if (c && !CODES.includes(c)) return new Error(`${row}There’s no VAT code ${c} switched on`);
      next[k] = c;
    }
  }
  if (input.is_active !== undefined) next.is_active = input.is_active;
  next.updated_at = new Date().toISOString();
  return next;
}

const put = (p: Product) => { store = [...store.filter((x) => x.code.toLowerCase() !== p.code.toLowerCase()), p]; };
const sorted = () => [...store].sort((a, b) => a.code.toLowerCase().localeCompare(b.code.toLowerCase()));

export const demoProducts: Part = {
  contactProducts: (id) => delay(theirList(id)),
  setContactProduct: (id, code, input) => {
    const p = demoProduct(code);
    if (!p) return fail(`There’s no item ${code}`);
    if (id.startsWith('s')) return fail('Their own prices apply to customers');
    const price = amount(input.price, 'price');
    if (price instanceof Error) return fail(price.message);
    const t: Theirs = { price, their_code: input.their_code?.trim() || null, their_description: input.their_description?.trim() || null };
    const mine = { ...(theirs[id] ?? {}) };
    if (!t.price && !t.their_code && !t.their_description) delete mine[p.code]; else mine[p.code] = t;
    theirs = { ...theirs, [id]: mine };
    return delay(theirList(id));
  },
  products: (includeInactive) => delay(sorted().filter((p) => p.is_active || includeInactive)),
  saveProduct: (code, input) => {
    const p = apply({ ...input, code });
    if (p instanceof Error) return fail(p.message);
    put(p);
    return delay(p);
  },
  setProductActive: (code, active) => {
    const p = demoProduct(code);
    if (!p) return fail(`There’s no item ${code}`);
    p.is_active = active;
    return delay(p);
  },
  deleteProduct: (code) => {
    const p = demoProduct(code);
    if (!p) return fail(`There’s no item ${code}`);
    if (p.in_use) return fail(`${p.code} is on invoices or bills, so it stays. Switch it off instead.`);
    store = store.filter((x) => x !== p);
    return delay(undefined);
  },
  importProducts: (rows) => {
    const errors: { row: number; code: string | null; error: string }[] = [];
    const seen = new Set<string>();
    const done: Product[] = [];
    let added = 0;
    let updated = 0;
    rows.forEach((r, i) => {
      const key = (r.code ?? '').trim().toLowerCase();
      if (seen.has(key)) { errors.push({ row: i + 1, code: r.code ?? null, error: `${r.code} appears more than once` }); return; }
      seen.add(key);
      const p = apply(r);
      if (p instanceof Error) { errors.push({ row: i + 1, code: r.code ?? null, error: p.message }); return; }
      if (demoProduct(p.code)) updated++; else added++;
      done.push(p);
    });
    if (errors.length) return delay({ added: 0, updated: 0, errors });
    done.forEach(put);
    return delay({ added, updated, errors: [] });
  },
};
